import { onMounted, ref } from 'vue'
import { captchaApi } from '@/services/captcha.api'

/**
 * 登入表單的圖形驗證碼。
 *
 * 每張驗證碼**用過即失效**（不論登入成功或失敗），所以任何一次送出之後
 * 都必須換新的一張，否則使用者第二次送出必定收到「驗證碼無效」，
 * 卻不知道自己哪裡做錯了。
 *
 * `immediate: false` 給**藏在對話框裡**的登入表單用：那份表單活在全站的
 * 對話框宿主上，掛載時間等於開站時間 —— 預設行為會讓每個訪客一進站
 * 就先跟後端要一張多數人永遠不會用到的驗證碼。改由開啟時自己 `refresh()`。
 */
export function useCaptcha(options: { immediate?: boolean } = {}) {
  /** 目前這張的 id。尚未取得時是空字串，送出前會被 schema 擋下 */
  const captchaId = ref('')
  const svg = ref<string | null>(null)
  const isLoading = ref(false)
  const failed = ref(false)

  async function refresh(): Promise<void> {
    isLoading.value = true
    failed.value = false

    try {
      const challenge = await captchaApi.issue()
      captchaId.value = challenge.id
      svg.value = challenge.svg
    } catch {
      // 取不到就讓畫面顯示重試，不要讓使用者對著空白框發呆
      captchaId.value = ''
      svg.value = null
      failed.value = true
    } finally {
      isLoading.value = false
    }
  }

  onMounted(() => {
    if (options.immediate !== false) void refresh()
  })

  return { captchaId, svg, isLoading, failed, refresh }
}
